import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { personLd, breadcrumbLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];

const MARCEL_PHOTO =
    "https://customer-assets-0z36b82j.emergentagent.net/job_marcel-coaching/artifacts/i4x1ckrv_marcel-strand.jpg";

// Terugval-teksten: alleen gebruikt als de content-API faalt of te traag is.
const DEFAULTS = {
    "over-marcel.seo-title": "Over Marcel van der Veen | Human Design Studio",
    "over-marcel.seo-description":
        "Marcel combineert Human Design met NLP, familieopstellingen en lichaamswerk. 25 jaar ervaring in persoonlijke ontwikkeling.",
    "over-marcel.hero.eyebrow": "Over Marcel",
    "over-marcel.hero.h1-a": "Ik ben",
    "over-marcel.hero.h1-accent": "Marcel van der Veen",
    "over-marcel.hero.intro":
        "Al 25 jaar verdiep ik me in persoonlijke ontwikkeling. Niet uit een boek, maar door het zelf te doen, te voelen en te ervaren. Mijn gereedschapskist is in die jaren goed gevuld: Human Design, NLP, familieopstellingen en lichaamswerk. Wat ze samenbindt is een vraag: hoe kom je dichter bij wie je echt bent?",
    "over-marcel.hero.foto": {
        src: MARCEL_PHOTO,
        alt: "Marcel van der Veen op het strand",
    },
    "over-marcel.verhaal.heading-a": "Mijn",
    "over-marcel.verhaal.heading-accent": "verhaal",
    "over-marcel.verhaal.body": [
        "Al 25 jaar verdiep ik me in persoonlijke ontwikkeling. Niet uit een boek, maar door het zelf te doen, te voelen en te ervaren. Mijn gereedschapskist is in die jaren goed gevuld: Human Design, NLP, familieopstellingen en lichaamswerk. Wat ze samenbindt is één vraag: hoe kom je dichter bij wie je echt bent?",
        "Het begon 25 jaar geleden met een opleiding NLP. Die gaf me veel inzicht in mezelf en in hoe ik met anderen omga. Het smaakte naar meer, dus deed ik de Master en daarna de Trainerstraining.",
        "Toch bleef er iets knagen. Er speelt zoveel mee in een leven dat je niet kunt zien of vatten. Daarom ging ik familieopstellingen doen, om de onderstroom te leren kennen: de patronen in familiesystemen die generaties lang doorwerken. Weer een stukje van de puzzel.",
        "Toen kwam ik lichaamswerk tegen. Dat kende ik nog niet. Met mijn hoofd had ik veel gedaan, met mijn lichaam eigenlijk nog niets. Een zomerweek werd een driejarige opleiding. Niet denken, maar voelen. Daar ben ik echt veel dichter bij mezelf gekomen.",
        "En toen vond ik Human Design. Dat maakte zoveel duidelijk. Ik heb een 6/2-profiel: de eerste dertig jaar vol ervaringen, niet allemaal leuk. Heel herkenbaar. Rond mijn dertigste trouwde ik en kregen we vier prachtige kinderen. Tussen mijn dertigste en vijftigste was mijn leven redelijk stabiel. Rond mijn vijftigste ging alles ondersteboven: een scheiding, een verhuizing, een nieuwe onderneming. Precies volgens de blauwdruk.",
        "Ik ben een Projector met veel openheid. Dat betekent veel ruimte om de energie van anderen over te nemen, en weinig eigen, constante energie. Ineens snapte ik waarom een baan van negen tot vijf bij mij nooit werkte. Zoveel dingen die ik nooit begreep, werden opeens helder. Inmiddels werk ik al twintig jaar als zelfstandig ondernemer. Zolang ik mijn eigen agenda en mijn energie kan volgen, gaat het goed. En ik zie mijn kinderen anders: ik kan hun blauwdruk lezen en ze daardoor beter begeleiden. Human Design heeft me zoveel gebracht. Dat wil ik doorgeven.",
    ],
    "over-marcel.methoden.eyebrow": "De aanpak",
    "over-marcel.methoden.heading-a": "Waarmee ik",
    "over-marcel.methoden.heading-accent": "werk",
    "over-marcel.methoden.intro": [
        "In een sessie gebruik ik Human Design als kaart. Maar ik wil niet dat je alleen informatie krijgt. Ik help je echt contact maken met je talenten en je uitdagingen.",
        "Daarvoor gebruik ik ook NLP, lichaamswerk en familieopstellingen. Zo ga je het niet alleen begrijpen, maar ook voelen en toepassen.",
    ],
    "over-marcel.methoden.1.naam": "Human Design",
    "over-marcel.methoden.1.desc": "De kaart. Je blauwdruk als startpunt van het gesprek.",
    "over-marcel.methoden.2.naam": "NLP",
    "over-marcel.methoden.2.desc": "Taal en patronen. Inzicht in hoe je denkt en communiceert.",
    "over-marcel.methoden.3.naam": "Familieopstellingen",
    "over-marcel.methoden.3.desc": "De onderstroom. Patronen die generaties lang doorwerken.",
    "over-marcel.methoden.4.naam": "Lichaamswerk",
    "over-marcel.methoden.4.desc": "Voelen in plaats van denken. Dichter bij jezelf komen.",
    "over-marcel.opleidingen.heading-a": "Mijn",
    "over-marcel.opleidingen.heading-accent": "opleidingen",
    "over-marcel.opleidingen.lijst": [
        "NLP Practitioner, Master Practitioner en Trainerstraining",
        "Tweejarige opleiding familieopstellingen, plus verdiepingen bij verschillende opstellers",
        "Driejarige opleiding lichaamswerk",
        "Diverse cursussen Human Design en veel eigen onderzoek in de afgelopen zes jaar",
        "Ingeschreven voor de officiële Human Design opleidingen, te volgen in 2026",
    ],
    "over-marcel.cta.heading": "Benieuwd wat Human Design jou kan brengen?",
    "over-marcel.cta.text":
        "Boek een sessie, of kom eerst vrijblijvend kennismaken tijdens de gratis Q&A.",
    "over-marcel.cta.boek": "Boek een sessie",
    "over-marcel.cta.qa": "Kom naar de gratis Q&A",
};

// Nummering van de methoden is structuur en blijft in de code
const METHODE_NRS = ["01", "02", "03", "04"];

const fadeUp = {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-10%" },
    transition: { duration: 0.9, ease: EASE },
};

export default function OverMarcel() {
    const c = usePageContent("over-marcel", DEFAULTS);
    useSeo({
        title: c ? c.t("over-marcel.seo-title") : DEFAULTS["over-marcel.seo-title"],
        description: c
            ? c.t("over-marcel.seo-description")
            : DEFAULTS["over-marcel.seo-description"],
        path: "/over-marcel",
        jsonLd: [personLd(), breadcrumbLd("Over Marcel", "/over-marcel")],
    });

    if (!c) return <ContentLoading />;

    const foto = c.img("over-marcel.hero.foto");
    const verhaal = c.list("over-marcel.verhaal.body");
    const methodIntro = c.list("over-marcel.methoden.intro");
    const methoden = METHODE_NRS.map((n, i) => ({
        n,
        name: c.t(`over-marcel.methoden.${i + 1}.naam`),
        desc: c.t(`over-marcel.methoden.${i + 1}.desc`),
    }));
    const opleidingen = c.list("over-marcel.opleidingen.lijst");

    return (
        <div data-testid="page-over-marcel">
            {/* ============ HERO ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pt-24 md:pt-40 pb-16 md:pb-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
                    {/* Left */}
                    <div className="md:col-span-7 order-2 md:order-1">
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: EASE }}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6"
                        >
                            {c.t("over-marcel.hero.eyebrow")}
                        </motion.p>

                        <div className="overflow-hidden">
                            <motion.h1
                                initial={{ y: "110%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 1.05,
                                    ease: EASE,
                                    delay: 0.05,
                                }}
                                className="font-[Fraunces] text-[10vw] md:text-[6.4vw] lg:text-[84px] leading-[0.98] tracking-[-0.02em] text-[var(--hds-ink)]"
                                style={{ fontWeight: 300 }}
                            >
                                {c.t("over-marcel.hero.h1-a")}{" "}
                                <em
                                    className="italic text-[var(--hds-clay)]"
                                    style={{ fontWeight: 300 }}
                                >
                                    {c.t("over-marcel.hero.h1-accent")}
                                </em>
                            </motion.h1>
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: EASE, delay: 0.4 }}
                            className="mt-8 md:mt-10 max-w-[58ch] text-[16.5px] md:text-[18px] leading-[1.65] text-[var(--hds-ink)]/82"
                        >
                            {c.t("over-marcel.hero.intro")}
                        </motion.p>
                    </div>

                    {/* Right: photo */}
                    <motion.div
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, ease: EASE, delay: 0.2 }}
                        className="md:col-span-5 order-1 md:order-2"
                    >
                        <div className="relative rounded-[14px] overflow-hidden border border-[var(--hds-line)] shadow-[0_30px_80px_rgba(44,39,34,0.12)]">
                            <img
                                src={foto.src}
                                alt={foto.alt}
                                data-testid="over-marcel-photo"
                                className="w-full h-auto object-cover aspect-[3/4]"
                                loading="eager"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ MIJN VERHAAL ============ */}
            <section
                className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32"
                data-testid="over-marcel-verhaal"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-4">
                        <motion.h2
                            {...fadeUp}
                            className="font-[Fraunces] text-[44px] md:text-[60px] leading-[1] tracking-[-0.02em] md:sticky md:top-28"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("over-marcel.verhaal.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {c.t("over-marcel.verhaal.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <div className="md:col-span-8 space-y-6 text-[16.5px] md:text-[18px] leading-[1.7] text-[var(--hds-ink)]/82 max-w-[64ch]">
                        {verhaal.map((p, i) => (
                            <motion.p
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-8%" }}
                                transition={{
                                    duration: 0.8,
                                    ease: EASE,
                                    delay: (i % 2) * 0.05,
                                }}
                                className={
                                    i === 0
                                        ? "first-letter:font-[Fraunces] first-letter:text-[64px] first-letter:leading-[0.7] first-letter:float-left first-letter:mr-3 first-letter:mt-2 first-letter:text-[var(--hds-clay)]"
                                        : ""
                                }
                            >
                                {p}
                            </motion.p>
                        ))}
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ WAARMEE IK WERK ============ */}
            <section
                className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32"
                data-testid="over-marcel-methoden"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-20">
                    <div className="md:col-span-5">
                        <motion.p
                            {...fadeUp}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {c.t("over-marcel.methoden.eyebrow")}
                        </motion.p>
                        <motion.h2
                            {...fadeUp}
                            transition={{
                                duration: 1,
                                ease: EASE,
                                delay: 0.05,
                            }}
                            className="font-[Fraunces] text-[44px] md:text-[60px] leading-[1] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("over-marcel.methoden.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {c.t("over-marcel.methoden.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                        className="md:col-span-7 md:pt-4 space-y-5 text-[16.5px] md:text-[17.5px] leading-[1.65] text-[var(--hds-ink)]/82 max-w-[60ch]"
                    >
                        {methodIntro.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </motion.div>
                </div>

                <div>
                    {methoden.map((m, i) => (
                        <motion.div
                            key={m.n}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8%" }}
                            transition={{
                                duration: 0.8,
                                ease: EASE,
                                delay: i * 0.05,
                            }}
                            data-testid={`over-marcel-methode-${i + 1}`}
                            className={`group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-10 items-baseline py-7 md:py-9 border-t border-[var(--hds-line)] ${
                                i === methoden.length - 1
                                    ? "border-b border-[var(--hds-line)]"
                                    : ""
                            }`}
                        >
                            <span
                                className="md:col-span-1 font-[Fraunces] text-[18px] text-[var(--hds-clay)]"
                                style={{
                                    fontWeight: 300,
                                    fontStyle: "italic",
                                }}
                            >
                                {m.n}
                            </span>
                            <h3
                                className="md:col-span-4 font-[Fraunces] text-[30px] md:text-[38px] leading-[1.05] tracking-[-0.01em] text-[var(--hds-ink)] transition-colors duration-500 group-hover:text-[var(--hds-clay-dark)]"
                                style={{ fontWeight: 300 }}
                            >
                                {m.name}
                            </h3>
                            <p className="md:col-span-7 text-[15.5px] md:text-[16.5px] leading-[1.6] text-[var(--hds-ink)]/70">
                                {m.desc}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="hds-hair" />
            </div>

            {/* ============ MIJN OPLEIDINGEN ============ */}
            <section
                className="mx-auto max-w-[1440px] px-5 md:px-10 py-20 md:py-32"
                data-testid="over-marcel-opleidingen"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-4">
                        <motion.h2
                            {...fadeUp}
                            className="font-[Fraunces] text-[44px] md:text-[60px] leading-[1] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("over-marcel.opleidingen.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {c.t("over-marcel.opleidingen.heading-accent")}
                            </em>
                        </motion.h2>
                    </div>
                    <div className="md:col-span-8">
                        <ul className="space-y-0">
                            {opleidingen.map((o, i) => (
                                <motion.li
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-8%" }}
                                    transition={{
                                        duration: 0.7,
                                        ease: EASE,
                                        delay: i * 0.05,
                                    }}
                                    className="flex items-start gap-5 py-5 border-b border-[var(--hds-line)] first:border-t first:border-[var(--hds-line)]"
                                >
                                    <span
                                        aria-hidden
                                        className="mt-2 shrink-0 inline-block w-2 h-2 rounded-full bg-[var(--hds-clay)]"
                                    />
                                    <span className="text-[16px] md:text-[17.5px] leading-[1.55] text-[var(--hds-ink)]/85">
                                        {o}
                                    </span>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ============ CTA-BLOK ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-40">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-8%" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    data-testid="over-marcel-cta"
                    className="relative overflow-hidden rounded-[18px] bg-[var(--hds-clay)] text-[var(--hds-card)] px-8 md:px-16 py-14 md:py-20"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -right-16 -top-16 w-[280px] h-[280px] rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
                        }}
                    />
                    <div className="relative max-w-[52ch]">
                        <h2
                            className="font-[Fraunces] text-[34px] md:text-[52px] leading-[1.05] tracking-[-0.02em] text-[var(--hds-card)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("over-marcel.cta.heading")}
                        </h2>
                        <p className="mt-6 text-[16px] md:text-[17.5px] leading-[1.6] text-[var(--hds-card)]/85 max-w-[46ch]">
                            {c.t("over-marcel.cta.text")}
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Button
                                to="/boeken"
                                data-testid="over-marcel-cta-boek"
                                variant="onClayPrimary"
                                icon="arrow"
                            >
                                {c.t("over-marcel.cta.boek")}
                            </Button>
                            <Button
                                to="/gratis-qa"
                                data-testid="over-marcel-cta-qa"
                                variant="onClaySecondary"
                            >
                                {c.t("over-marcel.cta.qa")}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
